import { isValid } from "date-fns";

import { ensureDate, type DateInput } from "./comparisons";
import { formatInTimeZone } from "../formatting";

/**
 * Calendar-day comparison utilities.
 *
 * Use these when the result is tied to date formatting in a specific timezone
 * (e.g. date picker grids, event date range text). The timezone parameter is
 * required so callers explicitly choose which calendar to use.
 *
 * Use instant comparison (isDateAfter, isDateBefore, isSameDate, etc. in
 * comparisons.ts) when you only care whether a moment in time has passed —
 * e.g. sale windows, event start/end, "is this in the past?".
 *
 * Rule: If the comparison result feeds into date formatting that uses a
 * timezone, the comparison must use the same timezone. Otherwise the
 * comparison and the formatted output can disagree about what day it is
 * (e.g. showing "Jan 17 - Jan 17" as a range when they are the same day
 * in the event timezone).
 */

/**
 * Are both dates on the same calendar day in the given timezone?
 * Callers must ensure both dates are defined; handle null/undefined before calling.
 */
export function isSameCalendarDay(
  date1: DateInput,
  date2: DateInput,
  timezone: string
): boolean {
  const d1 = ensureDate(date1);
  const d2 = ensureDate(date2);
  if (!isValid(d1) || !isValid(d2)) return false;

  const day1 = formatInTimeZone(d1, timezone, "yyyy-MM-dd");
  const day2 = formatInTimeZone(d2, timezone, "yyyy-MM-dd");
  return day1 === day2;
}

/**
 * Is date1 on the same or an earlier calendar day than date2 in the given timezone?
 * Callers must ensure both dates are defined; handle null/undefined before calling.
 */
export function isSameOrBeforeCalendarDay(
  date1: DateInput,
  date2: DateInput,
  timezone: string
): boolean {
  const d1 = ensureDate(date1);
  const d2 = ensureDate(date2);
  if (!isValid(d1) || !isValid(d2)) return false;

  const day1 = formatInTimeZone(d1, timezone, "yyyy-MM-dd");
  const day2 = formatInTimeZone(d2, timezone, "yyyy-MM-dd");
  return day1 <= day2;
}
